import type { ReactNode } from 'react';

type MetricTone = 'emerald' | 'sky' | 'amber' | 'orange' | 'red' | 'slate';
type MetricSize = 'sm' | 'md' | 'lg';

interface ImportantMetricProps {
  label: string;
  value: ReactNode;
  note?: string;
  tone?: MetricTone;
  size?: MetricSize;
  className?: string;
  onClick?: () => void;
  icon?: ReactNode;
}

const TONE_MAP: Record<MetricTone, { value: string; note: string }> = {
  emerald: { value: 'text-agrida-600', note: 'text-agrida-400' },
  sky: { value: 'text-sky-600', note: 'text-sky-400' },
  amber: { value: 'text-amber-600', note: 'text-amber-400' },
  orange: { value: 'text-orange-600', note: 'text-orange-400' },
  red: { value: 'text-red-600', note: 'text-red-400' },
  slate: { value: 'text-slate-700', note: 'text-slate-400' },
};

const SIZE_MAP: Record<MetricSize, { label: string; value: string; note: string; spacing: string }> = {
  sm: { label: 'text-[10px]', value: 'text-sm', note: 'text-[10px]', spacing: 'space-y-0' },
  md: { label: 'text-[11px]', value: 'text-base', note: 'text-[10px]', spacing: 'space-y-0.5' },
  lg: { label: 'text-xs', value: 'text-lg', note: 'text-[11px]', spacing: 'space-y-0.5' },
};

export function ImportantMetric({
  label,
  value,
  note,
  tone = 'slate',
  size = 'md',
  className = '',
  onClick,
  icon,
}: ImportantMetricProps) {
  const toneColors = TONE_MAP[tone];
  const sizeStyles = SIZE_MAP[size];

  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`min-w-0 text-center ${sizeStyles.spacing} ${onClick ? 'cursor-pointer active:scale-[0.98] transition-transform' : ''} ${className}`}
    >
      <div className="flex items-center justify-center gap-1.5">
        {icon && <span className="shrink-0 w-4 h-4 flex items-center justify-center">{icon}</span>}
        <span className={`${sizeStyles.label} font-medium text-slate-400 uppercase tracking-wide leading-tight text-safe-normal`}>
          {label}
        </span>
      </div>
      <p className={`${sizeStyles.value} font-bold leading-tight text-safe-normal ${toneColors.value}`}>
        {value}
      </p>
      {note && (
        <p className={`${sizeStyles.note} italic ${toneColors.note} leading-snug text-safe-normal`}>
          {note}
        </p>
      )}
    </Tag>
  );
}
